import { useEffect, useState } from "react";
import { create } from "zustand";

const STORAGE_KEY = "debtsure-sidebar-collapsed";

const readInitial = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    /* no-op */
  }
  return false;
};

const persist = (collapsed: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  } catch {
    /* no-op */
  }
};

interface SidebarState {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  toggleCollapsed: () => void;
  /** Below `lg` the sidebar is an off-canvas drawer; this is whether it's open. */
  mobileOpen: boolean;
  setMobileOpen: (value: boolean) => void;
}

/**
 * Sidebar collapse state.
 *
 * Persisted the same way as the theme: a collapsed sidebar is a workspace
 * preference, and having it spring back open on every reload defeats the point.
 * Lives in a store rather than local state because both the sidebar and the layout
 * that sizes it need to read it.
 */
export const useSidebar = create<SidebarState>((set) => ({
  collapsed: readInitial(),
  setCollapsed: (value) => {
    persist(value);
    set({ collapsed: value });
  },
  toggleCollapsed: () =>
    set((prev) => {
      const next = !prev.collapsed;
      persist(next);
      return { collapsed: next };
    }),
  mobileOpen: false,
  setMobileOpen: (value) => set({ mobileOpen: value }),
}));

const MOBILE_QUERY = "(max-width: 1023px)";

/** True below Tailwind's `lg` breakpoint, where the sidebar becomes a drawer. */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
};
